import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useAPIAuth } from '../../providers/APIAuthProvider';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';

const getFormSchema = (t: (key: string) => string) => z.object({
  first_name: z.string().min(1, {
    message: t('auth.validation.first_name_required'),
  }),
  last_name: z.string().min(1, {
    message: t('auth.validation.last_name_required'),
  }),
  email: z.string().email({
    message: t('auth.validation.email_invalid'),
  }),
  password: z.string().min(6, {
    message: t('auth.validation.password_min_length_6'),
  }),
  confirmPassword: z.string().min(1, {
    message: t('auth.validation.confirm_password_required'),
  }),
}).refine((data) => data.password === data.confirmPassword, {
  message: t('auth.validation.password_mismatch'),
  path: ["confirmPassword"],
});

interface RegisterFormProps {
  onSwitchToLogin?: () => void;
  onRegistrationSuccess?: () => void;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({
  onSwitchToLogin,
  onRegistrationSuccess
}) => {
  const { error, isLoading } = useAPIAuth();
  const { t } = useLanguage();

  const form = useForm<z.infer<ReturnType<typeof getFormSchema>>>({
    resolver: zodResolver(getFormSchema(t)),
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (_values: z.infer<ReturnType<typeof getFormSchema>>) => {
    try {
      // Note: API Auth doesn't have registration functionality yet
      // This is a placeholder for future implementation

      // Registration successful
      if (onRegistrationSuccess) {
        onRegistrationSuccess();
      }
    } catch (error) {
      // Error is handled by the auth provider
      console.error('Registration failed:', error);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="max-w-md w-full space-y-8 p-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-foreground mb-2">
            {t('auth.register.title')}
          </h2>
          <p className="text-muted-foreground">
            {t('auth.register.subtitle')}
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="mt-8 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex">
                  <div className="text-red-500 text-sm">
                    ⚠️ {error}
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('auth.register.first_name')}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t('auth.register.first_name_placeholder')}
                          autoComplete="given-name"
                          disabled={isLoading}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('auth.register.last_name')}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={t('auth.register.last_name_placeholder')}
                          autoComplete="family-name"
                          disabled={isLoading}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth.register.email')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('auth.register.email_placeholder')}
                        type="email"
                        autoComplete="email"
                        disabled={isLoading}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth.register.password')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('auth.register.password_placeholder')}
                        type="password"
                        autoComplete="new-password"
                        disabled={isLoading}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('auth.register.confirm_password')}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t('auth.register.confirm_password_placeholder')}
                        type="password"
                        autoComplete="new-password"
                        disabled={isLoading}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 rounded-lg font-medium"
              size="lg"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-foreground mr-2"></div>
                  {t('auth.register.loading')}
                </div>
              ) : (
                t('auth.register.submit')
              )}
            </Button>

            {onSwitchToLogin && (
              <div className="text-center">
                <div className="text-sm text-muted-foreground">
                  {t('auth.register.has_account')}{' '}
                  <Button
                    type="button"
                    variant="link"
                    onClick={onSwitchToLogin}
                    disabled={isLoading}
                    className="p-0 h-auto font-medium"
                  >
                    {t('auth.register.login_link')}
                  </Button>
                </div>
              </div>
            )}
          </form>
        </Form>
      </div>
    </div>
  );
};